import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FocusLock from 'react-focus-lock';

import { useUserGeneratedContent } from '../../../../models/user-generated-contents/context';
import { usePatronSelector } from '../../../../common/use-patron-selector';
import { channelSelectors } from '../../../../models/channels';
import { getChannelType } from '../../../content-submission/channels-popover/hooks';
import { Icon } from '../../../../components/ui';
import ChannelsPopover from '../../../content-submission/channels-popover/channels-popover';
import useOutsideClick from '../../../../common/use-outside-click';
import './topic-picker-card.scss';

const POPOVER_ID = 'ugc-topic-picker-popover';

const TopicPickerCard = () => {
  const { t } = useTranslation();
  const { userGeneratedContent, updateUserGeneratedContentFormState } =
    useUserGeneratedContent();
  const selectedIds = userGeneratedContent.topic_ids ?? [];

  const allChannels = usePatronSelector(
    channelSelectors.getChannelsWithoutHome
  );
  const selectableChannels = allChannels.filter(
    (c) => getChannelType(c) !== null
  );

  const [isOpen, setIsOpen] = useState(false);

  const handleApply = (ids: number[]) => {
    updateUserGeneratedContentFormState({ topic_ids: ids });
  };

  const containerRef = useOutsideClick({
    disabled: !isOpen,
    onClick: () => setIsOpen(false),
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const selectedChannels = selectableChannels.filter((c) =>
    selectedIds.includes(c.id)
  );

  return (
    <div
      className="ugc-topic-picker-card"
      ref={containerRef}
      onKeyDown={handleKeyDown}
    >
      <div className="ugc-topic-picker-card__label">
        {t('user_generated_content.send_to_topic')}
      </div>

      <div className="ugc-topic-picker-card__trigger-area">
        <button
          type="button"
          className="ugc-topic-picker-card__trigger"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-controls={POPOVER_ID}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <span className="ugc-topic-picker-card__trigger-channel">
            {selectedChannels.length > 0
              ? selectedChannels[0].name
              : t('user_generated_content.select_topics')}
          </span>
          {selectedChannels.length > 1 && (
            <span className="ugc-topic-picker-card__trigger-count">
              +{selectedChannels.length - 1}
            </span>
          )}
          <Icon type="add" />
        </button>

        {isOpen && (
          <FocusLock>
            <ChannelsPopover
              id={POPOVER_ID}
              suggestedChannels={[]}
              channels={selectableChannels}
              initialSelectedIds={selectedIds}
              onApply={handleApply}
              onClose={() => setIsOpen(false)}
            />
          </FocusLock>
        )}
      </div>
    </div>
  );
};

export default TopicPickerCard;
